import { useEffect, useRef } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

/**
 * DriverTour - A wrapper around Driver.js that provides the same API as react-joyride
 * with smooth Framer Motion-inspired animations.
 *
 * This component replicates the react-joyride UI/UX while using Driver.js under the hood
 * for better animation control and customization.
 */
const DriverTour = ({
  steps = [],
  run = false,
  onCallback = null,
  showProgress = true,
  showButtons = ['next', 'previous', 'close'],
  ...driverOptions
}) => {
  const driverObjRef = useRef(null);
  const previousRunRef = useRef(run);

  useEffect(() => {
    // Only initialize if run changes from false to true
    if (run && !previousRunRef.current) {
      // Convert react-joyride style steps to driver.js format
      const driverSteps = steps.map((step, index) => {
        const isLastStep = index === steps.length - 1;
        const isFirstStep = index === 0;

        return {
          element: step.target || step.element,
          popover: {
            title: step.title || '',
            description: step.content || step.description || '',
            side: step.placement === 'top' ? 'top' :
                  step.placement === 'bottom' ? 'bottom' :
                  step.placement === 'left' ? 'left' :
                  step.placement === 'right' ? 'right' : 'bottom',
            align: 'start',
          },
        };
      });

      // Initialize Driver.js with custom configuration
      const driverObj = driver({
        animate: true, // Enable smooth CSS animations
        showProgress,
        overlayOpacity: 0.5,
        stagePadding: 10,
        popoverOffset: 10,
        smoothScroll: true,

        // Progress text template
        progressText: showProgress ? 'Step {{current}} of {{total}}' : '',

        // Custom button text
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Finish',

        // Allow clicking overlay to close (react-joyride behavior)
        allowClose: true,
        overlayClickNext: false,

        steps: driverSteps,

        // Lifecycle hooks to match react-joyride callbacks
        onPopoverRender: (popover, { config, state }) => {
          const { activeIndex, activeStep } = state;

          // Add Skip Walkthrough button if needed
          if (showButtons.includes('close')) {
            const footer = popover.footerButtons;

            // Add skip button (styled like react-joyride)
            const skipBtn = document.createElement('button');
            skipBtn.className = 'driver-popover-skip-btn';
            skipBtn.innerText = 'Skip Walkthrough';
            skipBtn.onclick = () => {
              if (onCallback) {
                onCallback({
                  type: 'tour:end',
                  status: 'skipped',
                  index: activeIndex,
                  action: 'skip',
                });
              }
              driverObj.destroy();
            };

            // Insert skip button before other buttons
            if (footer && footer.firstChild) {
              footer.insertBefore(skipBtn, footer.firstChild);
            }
          }
        },

        onNextClick: (element, step, options) => {
          const { state } = options;
          const isLastStep = state.activeIndex === driverSteps.length - 1;

          if (isLastStep) {
            // On last step, trigger callback and close
            if (onCallback) {
              onCallback({
                type: 'step:after',
                index: state.activeIndex,
                action: 'next',
                status: 'finished',
              });
            }
            driverObj.destroy();
          } else {
            // On non-last steps, move to next and trigger callback
            driverObj.moveNext();

            if (onCallback) {
              onCallback({
                type: 'step:after',
                index: state.activeIndex,
                action: 'next',
                status: 'running',
              });
            }
          }
        },

        onPrevClick: (element, step, options) => {
          const { state } = options;

          if (onCallback) {
            onCallback({
              type: 'step:before',
              index: state.activeIndex,
              action: 'prev',
            });
          }

          driverObj.movePrevious();
        },

        onCloseClick: (element, step, options) => {
          const { state } = options;

          if (onCallback) {
            onCallback({
              type: 'tour:end',
              status: 'skipped',
              index: state.activeIndex,
              action: 'close',
            });
          }

          driverObj.destroy();
        },

        onDestroyStarted: () => {
          const state = driverObj.getState();
          const isLastStep = state?.activeIndex === driverSteps.length - 1;

          if (onCallback && isLastStep) {
            onCallback({
              type: 'tour:end',
              status: 'finished',
              index: state.activeIndex,
              action: 'next',
            });
          }
        },

        onDestroyed: () => {
          // Cleanup
        },

        ...driverOptions,
      });

      driverObjRef.current = driverObj;

      // Start the tour with a slight delay for smoother initial animation
      setTimeout(() => {
        driverObj.drive();

        // Trigger initial callback
        if (onCallback) {
          onCallback({
            type: 'tour:start',
            status: 'running',
            index: 0,
          });
        }
      }, 100);
    }

    // Destroy tour if run changes from true to false
    if (!run && previousRunRef.current && driverObjRef.current) {
      driverObjRef.current.destroy();
      driverObjRef.current = null;
    }

    previousRunRef.current = run;

    // Cleanup on unmount
    return () => {
      if (driverObjRef.current) {
        driverObjRef.current.destroy();
        driverObjRef.current = null;
      }
    };
  }, [run, steps, showProgress, showButtons, onCallback, driverOptions]);

  return null; // This is a headless component
};

export default DriverTour;
